import {
  booleanAttribute,
  ChangeDetectionStrategy,
  Component,
  computed,
  ContentChild,
  Input,
  TemplateRef
} from '@angular/core';
import {Observable} from "rxjs";
import {
  AbstractGenericListHandler,
  ConfigureList,
  GenericTableColumn,
  GenericTableComponent,
  GenericTableEmptyTemplateDirective,
  GenericTableFilter,
  GenericTableFooterTemplateDirective,
  GenericTableHeaderTemplateDirective,
  GenericTableRowActionTemplateDirective,
  GenericTableRowTemplateDirective,
  GenericTableService,
  PaginatorComponent
} from "../../index";
import {CommonModule} from "@angular/common";
import {TranslateModule} from "@ngx-translate/core";
import {InputSearchComponent} from "../input-search/input-search.component";
import {CardComponent, CardFooterDirective, CardToolbarDirective} from "../../../ui/app-card";

@Component({
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'app-generic-table-page',
  template: `
    <app-card [cardTitle]="title" [blocked]="tableIsLoading()">

      <ng-template cardToolbar>
        @if (cardToolbarTemplateRef && prependActions) {
          <ng-template
            [ngTemplateOutlet]="cardToolbarTemplateRef"
            [ngTemplateOutletContext]="{
                       tableSearchFormGroup: tableSearchFormGroup,
                       tableSearchFormControlName: tableSearchFormControlName,
                       tableIsFilterActive: tableIsFilterActive(),
                      }">
          </ng-template>
        }

        @if (showRefreshBtn) {
          <a type="button" class="btn btn-secondary" (click)="applyRefreshPatch()">
            @if (showRefreshBtnIcon) {
              <i class="{{refreshBtnIcon}}"></i>
            }
            {{ refreshBtnTitle | translate }}
          </a>
        }

        @if (cardToolbarTemplateRef && !prependActions) {
          <ng-template
            [ngTemplateOutlet]="cardToolbarTemplateRef"
            [ngTemplateOutletContext]="{
                             tableSearchFormGroup: tableSearchFormGroup,
                             tableSearchFormControlName: tableSearchFormControlName,
                             tableIsFilterActive: tableIsFilterActive(),
                            }">
          </ng-template>
        }
      </ng-template>

      @if (cardFooterTemplateRef) {
        <ng-template cardFooter>
          <ng-template [ngTemplateOutlet]="cardFooterTemplateRef"></ng-template>
        </ng-template>
      }

      @if (enableGlobalSearch) {
        <app-input-search
          [formGroup]="tableSearchFormGroup"
          [controlName]="tableSearchFormControlName"
          [placeholder]="globalSearchPlaceholder"
          [class]="globalSearchNgClass">
        </app-input-search>
      }


      @if (tableCaptionTemplate) {
        <div class="">
          <ng-template
            [ngTemplateOutlet]="tableCaptionTemplate"
            [ngTemplateOutletContext]="{
               tableSearchFormGroup: tableSearchFormGroup,
               tableSearchFormControlName: tableSearchFormControlName,
               tableIsFilterActive: tableIsFilterActive(),
              }"
          >
          </ng-template>
        </div>
      }


      <div style="margin-top: 2.5rem">
        <app-generic-table
          [columns]="columns"

          [noDataMessage]="internalDataMessage()"
          [showTableIfEmpty]="showTableIfEmpty"

          [data]="tableDataSource()"
          [showSpinner]="false"

          [sortState]="tableState().sorting"
          (sortChange)="applySortPatch($event)"

          [striped]="tableStriped"
          [rounded]="tableRounded"
          [bordered]="tableBordered"
          [flushStyles]="tableFlushStyles"
          [showRowBorder]="tableShowRowBorder"
          [rowHoverable]="tableRowHoverable"
        >

          @if (headersTemplateRef) {
            <ng-template genTableHeader let-data="data">
              <ng-template
                [ngTemplateOutlet]="headersTemplateRef"
                [ngTemplateOutletContext]="{$implicit: columns, data: data }"
              ></ng-template>
            </ng-template>
          }

          @if (rowActionTemplateRef) {
            <ng-template genTableRowAction let-row>
              <ng-template
                [ngTemplateOutlet]="rowActionTemplateRef"
                [ngTemplateOutletContext]="{$implicit: row}"
              ></ng-template>
            </ng-template>
          }

          @if (rowTemplateRef) {
            <ng-template genTableRow
                         let-row
                         let-index="index"
                         let-first="first"
                         let-last="last"
                         let-odd="odd"
                         let-even="even"
                         let-count="count"
            >
              <ng-template
                [ngTemplateOutlet]="rowTemplateRef"
                [ngTemplateOutletContext]="{
                                      $implicit: row,
                                      index,
                                      first,
                                      last,
                                      odd,
                                      even,
                                      count
                                  }"
              ></ng-template>

            </ng-template>
          }

          @if (emptyTableTemplateRef) {
            <ng-template genTableEmpty>
              <ng-template
                [ngTemplateOutlet]="emptyTableTemplateRef"
                [ngTemplateOutletContext]="{$implicit: noDataMessage}"
              ></ng-template>
            </ng-template>
          }

          @if (footerActionTemplateRef) {
            <ng-template genTableFooter let-rows>
              <ng-template
                [ngTemplateOutlet]="footerActionTemplateRef"
                [ngTemplateOutletContext]="{$implicit: rows}"
              ></ng-template>
            </ng-template>
          }

        </app-generic-table>
      </div>

      @if (enablePagination) {
        <app-paginator
          class="d-flex justify-content-between align-items-center flex-wrap"
          [paginatorState]="tableState().paginator"
          (paginateChange)="applyPaginatorPatch($event)"
          [showLoading]="paginatorShowLoading"
          [showPageSize]="paginatorShowPageSize"
          [showPages]="paginatorShowPages"
          [showPageInfo]="paginatorShowPageInfo"
        ></app-paginator>
      }

    </app-card>
  `,
  styles: [``],
  imports: [
    CommonModule,
    TranslateModule,
    CardComponent,
    PaginatorComponent,
    GenericTableComponent,
    InputSearchComponent,
    GenericTableHeaderTemplateDirective,
    GenericTableRowActionTemplateDirective,
    GenericTableRowTemplateDirective,
    GenericTableFooterTemplateDirective,
    CardToolbarDirective,
    CardFooterDirective,
    GenericTableEmptyTemplateDirective,
    CardToolbarDirective
  ]
})
/**
 * @author Carlos Duardo <carlos.duardo@qualud.com>
 */
export class GenericTablePageComponent extends AbstractGenericListHandler {
  //general page
  @Input() tableFilter$: Observable<GenericTableFilter>;

  @Input() title: string = 'Listado';

  @Input({transform: booleanAttribute}) showRefreshBtn: boolean = true;
  @Input() refreshBtnTitle: string = 'ACTION.RELOAD.GENERIC';
  @Input({transform: booleanAttribute}) showRefreshBtnIcon: boolean = true;
  @Input() refreshBtnIcon: string = 'fa fa-refresh';

  @Input({transform: booleanAttribute}) prependActions: boolean = false;

  @Input() tableCaptionTemplate: TemplateRef<any>;

  //generic table options
  @Input({required: false}) columns: GenericTableColumn[];
  @Input({required: true}) tableService: GenericTableService;

  @ContentChild(GenericTableHeaderTemplateDirective, {read: TemplateRef})
  headersTemplateRef?: TemplateRef<any>;
  @ContentChild(GenericTableRowTemplateDirective, {read: TemplateRef})
  rowTemplateRef?: TemplateRef<any>;
  @ContentChild(GenericTableRowActionTemplateDirective, {read: TemplateRef})
  rowActionTemplateRef?: TemplateRef<any>;
  @ContentChild(GenericTableFooterTemplateDirective, {read: TemplateRef})
  footerActionTemplateRef?: TemplateRef<any>;
  @ContentChild(GenericTableEmptyTemplateDirective, {read: TemplateRef})
  emptyTableTemplateRef?: TemplateRef<any>;

  @ContentChild(CardFooterDirective, {read: TemplateRef})
  cardFooterTemplateRef?: TemplateRef<any>;
  @ContentChild(CardToolbarDirective, {read: TemplateRef})
  cardToolbarTemplateRef?: TemplateRef<any>;

  @Input({transform: booleanAttribute}) tableStriped: boolean = false;
  @Input({transform: booleanAttribute}) tableRounded: boolean = false;
  @Input({transform: booleanAttribute}) tableBordered: boolean = false;
  @Input({transform: booleanAttribute}) tableFlushStyles: boolean = false;
  @Input({transform: booleanAttribute}) tableShowRowBorder: boolean = false;
  @Input({transform: booleanAttribute}) tableRowHoverable: boolean = true;

  //no data options
  @Input() noDataMessage: string = 'Lo sentimos no hay información disponible.';
  @Input() noDataMessageOnFilter: string = 'No se encontraron elementos.';
  @Input() noDataMessageOnLoading: string = 'Cargando';
  @Input({transform: booleanAttribute}) showTableIfEmpty: boolean = true;

  //pagination options
  @Input({transform: booleanAttribute}) enablePagination: boolean = true;
  @Input({transform: booleanAttribute}) paginatorShowLoading: boolean = true;
  @Input({transform: booleanAttribute}) paginatorShowPageSize: boolean = true;
  @Input({transform: booleanAttribute}) paginatorShowPages: boolean = true;
  @Input({transform: booleanAttribute}) paginatorShowPageInfo: boolean = true;

  //global search
  @Input({transform: booleanAttribute}) enableGlobalSearch: boolean = true;
  @Input() globalSearchPlaceholder: string = 'ACTION.SEARCH.GENERIC';
  @Input() globalSearchNgClass: string;
  @Input() globalSearchKeyUpDelay: number = 650;

  internalDataMessage = computed(() => {
    return this.tableIsFilterActive() ? this.noDataMessageOnFilter : this.noDataMessage
  })

  configureList(): ConfigureList {
    return {
      tableService: this.tableService,
      reactiveFilters: this.tableFilter$,
      searchFilter: {
        enabled: this.enableGlobalSearch,
        inputDelay: this.globalSearchKeyUpDelay
      }
    };
  }

}
